<template>
  <div
    :class="{
      'has-avatar': !isHideAvatar || isKeepAvatarDesign,
      'bottom-navigation-enabled': currentConfig.isBottomNavigationEnabled,
    }"
    class="main-layout"
  >
    <div
      v-if="overlayColor"
      :style="{ backgroundColor: overlayColor }"
      :class="{ 'hide-on-portrait': isIndependentVerticalBg }"
      class="overlay"
    />
    <div
      v-if="isIndependentVerticalBg && currentContent.styles?.verticalOverlay && hasVMedia"
      :style="{ backgroundColor: currentContent.styles.verticalOverlay }"
      class="v-overlay"
    />
    <div
      ref="content"
      :class="{
        'has-scroll': hasScroll,
        'has-avatar': !isHideAvatar || isKeepAvatarDesign,
        'central-design': useCenterVerticalDesktopDesign || (isHideAvatar && !isKeepAvatarDesign),
        'bottom-navigation-enabled': currentConfig.isBottomNavigationEnabled,
        'is-website': currentContent.type === 'elements.WEBSITE',
      }"
      class="content"
    >
      <r2r-chat
        v-if="isR2RChat"
      />
      <question-element
        v-else-if="currentContent.type === 'elements.QUESTION'"
        :key="seqContentKey"
        class="slide-content"
      />
      <feedback-element
        v-else-if="currentContent.type === 'elements.FEEDBACK'"
        :key="seqContentKey"
      />
      <media-element
        v-else-if="currentContent.type === 'elements.MEDIA'"
        :key="seqContentKey"
      />
      <menu-element
        v-else-if="currentContent.type === 'elements.PROJECT_MENU'"
        :key="seqContentKey"
      />
      <user-input-element
        v-else-if="currentContent.type === 'elements.USER_INPUT'"
        :key="seqContentKey"
      />
      <website-element
        v-else-if="currentContent.type === 'elements.WEBSITE'"
        :key="seqContentKey"
      />
      <qr-reader-element
        v-else-if="isQrReader"
      />
      <knowledge-base-ui-element
        v-else-if="currentContent.type === 'elements.KNOWLEDGE_BASE_UI'"
        :key="seqContentKey"
        @processing="$emit('processing', $event)"
      />
    </div>
  </div>
  <subtitles />
</template>

<script>
import {
  defineComponent,
  computed,
  ref,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import useContent from '@/composables/useContent';
import useActions from '@/composables/useActions';
import useR2RChat from '@/composables/useR2RChat';
import useConfig from '@/composables/useConfig';
import QuestionElement from '@/components/elements/Question';
import WebsiteElement from '@/components/elements/Website';
import FeedbackElement from '@/components/elements/Feedback';
import MediaElement from '@/components/elements/Media';
import MenuElement from '@/components/elements/Menu';
import QrReaderElement from '@/components/elements/QrCode';
import UserInputElement from '@/components/elements/user-input/UserInput.vue';
import KnowledgeBaseUiElement from '@/components/elements/KnowledgeBaseUI.vue';
import R2RChat from '@/components/elements/R2RChat';
import Subtitles from '@/components/Subtitles';
import ACTIONS from '@/actions/types';

export default defineComponent({
  name: 'FlowPage',

  emits: ['processing'],

  constants: {
    WEBSITE_ACTION: ACTIONS.WEBSITE,
    API_CALL_ACTION: ACTIONS.API_CALL,
    NAVIGATION: ACTIONS.NAVIGATION,
  },

  components: {
    UserInputElement,
    QrReaderElement,
    QuestionElement,
    FeedbackElement,
    WebsiteElement,
    MediaElement,
    MenuElement,
    KnowledgeBaseUiElement,
    'r2r-chat': R2RChat,
    Subtitles,
  },

  setup() {
    const {
      currentContent,
      isHideAvatar,
      isKeepAvatarDesign,
      isIndependentVerticalBg,
      useCenterVerticalDesktopDesign,
      media,
      vMedia,
    } = useContent();
    const { currentAction } = useActions();
    const { isChatInProgress } = useR2RChat();
    const { defaultStyles, currentConfig } = useConfig();
    const content = ref(null);
    const hasScroll = ref(false);

    const seqContentKey = computed(() => `${currentContent.value.id}_${currentContent.value.uniqId}`);
    const overlayColor = computed(() => (currentContent.value?.styles
      ? (currentContent.value?.styles?.overlay)
      : defaultStyles.value.overlay));

    const hasMedia = computed(() => media.value?.filename
      || media.value?.externalUrl);
    const hasVMedia = computed(() => vMedia.value?.filename
        || vMedia.value?.externalUrl);
    const isQrReader = computed(() => currentAction.value?.name === ACTIONS.QR_READER && !currentContent.value?.id);
    const isR2RChat = computed(() => isChatInProgress.value);

    async function checkForScroll() {
      await nextTick();
      hasScroll.value = content.value?.scrollHeight > content.value?.clientHeight;
    }

    watch(currentContent, async () => {
      await checkForScroll();
      await nextTick();
      if (content.value) {
        content.value.scrollTop = 0;
      }
    }, { deep: true });

    onMounted(() => {
      checkForScroll();
      window.addEventListener('resize', checkForScroll);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', checkForScroll);
    });

    return {
      currentConfig,
      currentContent,
      isHideAvatar,
      isKeepAvatarDesign,
      currentAction,
      content,
      hasScroll,
      hasMedia,
      hasVMedia,
      isIndependentVerticalBg,
      isQrReader,
      isR2RChat,
      seqContentKey,
      overlayColor,
      useCenterVerticalDesktopDesign,
    };
  },
});
</script>

<style lang="scss" scoped>
.main-layout {
  width: 100%;
  height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 0;

  @media screen and (orientation: portrait) {
    height: 100%;

    @media screen and (min-height: $big_vertical_screen_breakpoint) {
      &.bottom-navigation-enabled {
        height: 100%;
      }
    }
  }

  &.has-avatar {
    @media screen and (orientation: landscape) and (min-width: $desktop_s_breakpoint) {
      width: 60%;
      margin-left: -40%;
    }

    @media screen and (max-height: $mobile_height_breakpoint) {
      width: 60%;
      margin-left: -40%;
    }

    @media screen and (min-height: $big_vertical_screen_breakpoint) {
      width: 100%;
      margin-left: auto;
    }
  }
}

.overlay {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 1;

  @media screen and (orientation: landscape) and (min-width: $desktop_s_breakpoint) {
    position: fixed;
  }

  @media screen and (max-height: $mobile_height_breakpoint) {
    position: fixed;
  }

  @media screen and (min-height: $big_vertical_screen_breakpoint) {
    position: absolute;
  }

  &.hide-on-portrait {
    @media screen and (orientation: portrait) {
      display: none;
    }
  }
}

.v-overlay {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 1;

  @media screen and (max-height: $mobile_height_breakpoint) {
    position: fixed;
  }

  @media screen and (min-height: $big_vertical_screen_breakpoint) {
    position: absolute;
  }

  @media screen and (orientation: landscape) {
    display: none;
  }
}

.content {
  position: relative;
  flex-grow: 2;
  z-index: 3;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: auto;
  min-height: 0;

  &.bottom-navigation-enabled {
    @media screen and (max-width: $desktop_s_breakpoint - 1px) {
      padding-bottom: 64px;
    }

    @media screen and (max-height: $mobile_height_breakpoint) {
      padding-bottom: 0;
    }

    @media screen and (min-height: $big_vertical_screen_breakpoint) {
      padding-bottom: 188px;
      margin-bottom: 188px;
    }
  }

  @media screen and (orientation: landscape) and (min-width: $desktop_s_breakpoint) {
    &:not(.bottom-navigation-enabled) {
      margin-top: 60px;
    }

    &.bottom-navigation-enabled {
      margin-bottom: 60px;
    }
  }

  @media screen and (orientation: portrait) {
    &:not(.bottom-navigation-enabled) {
      margin-top: 98px;
    }
  }

  @media screen and (orientation: portrait) and (min-width: $desktop_s_breakpoint - 1px) {
    &:not(.bottom-navigation-enabled) {
      margin-top: 118px;
    }
  }

  @media screen and (min-width: $tv_screen_breakpoint) {
    &:not(.bottom-navigation-enabled) {
      margin-top: 174px;
    }
  }

  @media screen and (min-height: $big_vertical_screen_breakpoint) {
    &:not(.bottom-navigation-enabled) {
      margin-top: 188px;
    }
  }

  &.has-avatar {
    @media screen and (max-width: $desktop_s_breakpoint - 1px) {
      justify-content: flex-end;
    }

    @media screen and (max-height: $mobile_height_breakpoint) {
      justify-content: center;
    }

    @media screen and (min-height: $big_vertical_screen_breakpoint) {
      position: static;
      max-height: none;
      justify-content: flex-end;
    }
  }

  &.central-design {
    @media screen and (orientation: portrait) and (min-width: $desktop_s_breakpoint - 1px) {
      justify-content: center;
    }
  }

  &:not(.has-avatar) {
    &.has-scroll {
      display: block;
    }
  }

  @media screen and (orientation: landscape) and (min-width: $desktop_s_breakpoint) {
    position: relative;
    justify-content: center;
    overflow: auto;

    &.has-scroll {
      top: $header_height;
      display: block;
      max-height: calc(100vh - $header_height);
      padding-bottom: $header_height;
    }
  }

  @media screen and (max-height: $mobile_height_breakpoint) {
    position: relative;
    justify-content: center;
    overflow: auto;

    margin-top: 40px;

    &.has-avatar {
      top: 0 !important;
    }

    &.has-scroll {
      top: $header_height;
      display: block;
      max-height: calc(100vh - $header_height);
    }

    &.bottom-navigation-enabled {
      margin-bottom: 64px; // bottom navigation has background color so we need to use whole height for margin
    }
  }
}
</style>
